/**
 * The semantic row state the server emits. Deliberately NOT a colour — the
 * page maps these to classes, so styling stays a client concern.
 */
export type PostOpsRowStatus =
  | 'alert'
  | 'attention'
  | 'discharged'
  | 'flagged'
  | 'plain';

/**
 * Shared constants for the Post-ops worklist.
 *
 * Kept out of the page component so the client/server seam test can import the
 * sort keys without pulling in React.
 */

/**
 * The sortable column keys, in the worklist's column order.
 *
 * These are the wire values sent as `sortBy`, and each one is also a
 * DataTable column `key` — the column key IS the sort key, which is what lets
 * DataTable's `onSortChange` hand the server something it accepts.
 *
 * MUST match `private const SORTS` in
 * api/app/Services/PostOps/PostOpsListService.php, in the same order.
 * postOpsSortKeys.test.ts reads that PHP file and fails if the two drift.
 * Four Surgery columns silently stopped sorting because each side only ever
 * tested itself against its own list.
 */
export const POST_OPS_SORT_KEYS = [
  'procedureDate',
  'patientName',
  'procedures',
  'lastVisit',
  'nextVisit',
  'status',
  'qa',
  'reason',
  'surveyResult',
  'photoConsent',
] as const;

export type PostOpsSortKey = (typeof POST_OPS_SORT_KEYS)[number];

export const DEFAULT_POST_OPS_SORT: PostOpsSortKey = 'procedureDate';
export const DEFAULT_POST_OPS_SORT_DIR: 'asc' | 'desc' = 'desc';

/**
 * The six sub-tabs.
 *
 * `id` is legacy's `pageID` and is sent to the server as `tab`. The ids are
 * deliberately non-contiguous — they are legacy's, and the server validates
 * against the same set.
 */
export const POST_OPS_TABS = [
  { id: 1, label: 'All' },
  { id: 3, label: 'In Follow Up' },
  { id: 7, label: 'Needs Follow-Up' },
  { id: 4, label: 'Q/A' },
  { id: 6, label: 'Discharged' },
  { id: 8, label: 'Revision' },
] as const;

export const DEFAULT_POST_OPS_TAB = 1;

/**
 * The filter parameter names this page puts on the wire.
 *
 * MUST be accepted by PostOpsListService -- either as a canonical key in
 * FILTER_KEYS or via FILTER_ALIASES. postOpsSortKeys.test.ts reads that PHP
 * and fails if the two drift.
 *
 * This seam is why the export silently returned every doctor's rows for a
 * single-doctor filter: the page spoke the plural names, the query read the
 * canonical ones, and nothing compared the two.
 */
export const POST_OPS_FILTER_PARAMS = [
  'doctorIds',
  'facilityIds',
  'reasonIds',
  'qaStatusIds',
  'procDateFrom',
  'procDateTo',
] as const;

/**
 * The six stat tiles, in render order.
 *
 * These deliberately do NOT line up with the tabs: "Discharged Happy" counts a
 * narrower set than the Discharged tab, "Lost to Follow-Up" has no tab, and
 * "Revision" has no tile. That asymmetry is legacy's and is preserved.
 */
export const POST_OPS_TILES = [
  { key: 'procedures', label: 'Procedures', tone: 'slate' },
  { key: 'inFollowUp', label: 'In Follow-Up', tone: 'blue' },
  { key: 'needsFollowUp', label: 'Needs Follow-Up', tone: 'amber' },
  { key: 'inQa', label: 'In Q/A', tone: 'violet' },
  { key: 'dischargedHappy', label: 'Discharged Happy', tone: 'green' },
  { key: 'lostToFollowUp', label: 'Lost to Follow-Up', tone: 'red' },
] as const;

/**
 * Row tint per semantic status.
 *
 * Mirrors legacy's bg-row-* classes: red for a complication or an unhappy
 * result, yellow for in-follow-up with no future appointment, green for
 * discharged happy, purple for discharged unhappy or red-flagged, and nothing
 * at all for the rest — including a plan with no post-op record yet.
 */
export const POST_OPS_ROW_STATUS_STYLES: Record<PostOpsRowStatus, string> = {
  alert: 'bg-red-50',
  attention: 'bg-amber-50',
  discharged: 'bg-emerald-50',
  flagged: 'bg-purple-50',
  plain: '',
};

/** The Q/A filter's two values, matching the server's qaStatus handling. */
export const POST_OPS_QA_STATUS_OPTIONS = [
  { value: '0', label: 'Open' },
  { value: '1', label: 'Resolved' },
];

/**
 * The search modes, in the order the selector offers them.
 *
 * MUST match PostOpsListService::SEARCH_TYPES; postOpsSortKeys.test.ts reads
 * the PHP and fails if they drift. Legacy offered this as the dropdown beside
 * the search box (postops_full_list-test.php:246-252), and the plan-id and
 * patient-id modes prefix-match the DISPLAY ids staff read off the screen.
 */
export const POST_OPS_SEARCH_TYPES = [
  { value: 'name', label: 'Name' },
  { value: 'planId', label: 'Plan ID' },
  { value: 'patientId', label: 'Patient ID' },
] as const;

export type PostOpsSearchType = (typeof POST_OPS_SEARCH_TYPES)[number]['value'];

/**
 * The query parameter negated filters travel under, as a list of sidebar keys --
 * the same wire shape every V5 list page uses (lib/filterRegistry.ts
 * negatedFilterKeys). Never a {key: boolean} map: legacy stores that shape with
 * the opposite meaning.
 */
export const POST_OPS_NEGATION_PARAM = 'filterPlus';

/** The sidebar filters whose +/- toggle the server honours. */
export const POST_OPS_NEGATABLE_FILTERS = ['doctor', 'facility', 'reason', 'qastatus'] as const;

/**
 * A date for display, or an em dash when there is none.
 *
 * Every date the server sends is a stored "Y-m-d H:i:s". Two parsing traps are
 * closed here rather than trusted to the browser:
 *   - a bare "Y-m-d" parses as UTC midnight, which is the PREVIOUS day in every
 *     US timezone -- the defect that made Next Visit render a day early;
 *   - a space-separated datetime is not ISO 8601, and Safari has historically
 *     refused it outright.
 * Both are anchored to local time explicitly.
 */
export function formatPostOpsDate(value: string | null | undefined): string {
  if (!value || value.startsWith('0000-00-00')) return '\u2014';

  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value.replace(' ', 'T');
  const parsed = new Date(iso);

  if (Number.isNaN(parsed.getTime())) return '\u2014';

  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
